import React, { useCallback, useRef } from 'react';
import { useDebounce } from 'react-use';

/**
 * Using a debounce effect combined with onBlur causes a race condition where the debounced value
 * can replace the value set by onBlur. This hook ensures that the debounced value is not set
 * after the onBlur event has fired.
 * see: example use in src/components/inputs/TextArea.tsx
 */
const useDebounceWithOnBlur = ({
  delay,
  field,
  doOnBlur
}): [
  React.MutableRefObject<React.ChangeEvent<HTMLTextAreaElement> | undefined>,
  (e: React.FocusEvent<HTMLTextAreaElement>) => void
] => {
  // this ref stores the event that triggered the debounce
  const changeEventRef = useRef<React.ChangeEvent<HTMLTextAreaElement>>();
  const { onBlur, onChange } = field;
  /**
   * Using a debounce effect for the input to avoid having the field calling handleChange on every key stroke.
   * ref:
   * https://medium.com/@vlad_19457/reducing-the-number-of-renders-when-using-formik-9790bf111ab9)
   * https://github.com/streamich/react-use/blob/master/docs/useDebounce.md
   */
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [isReady, cancel] = useDebounce(
    () => {
      if (changeEventRef.current) {
        onChange(changeEventRef.current);
      }
    },
    delay,
    [changeEventRef.current]
  );

  // Add a "blur" event that will automatically trim all whitespaces from left & right sides of the string followed by triggering the original "field" onBlur
  const handleOnBlur = useCallback(
    (e: React.FocusEvent<HTMLTextAreaElement>) => {
      e.preventDefault();

      if (changeEventRef.current) {
        // if there's a pending debounce, cancel it
        if (!isReady()) cancel();

        // run the thing you want to run to set the onblur
        // eg. (eventRefCurrent) => { eventRefCurrent.target.value = eventRefCurrent.target.value.trim(); }
        doOnBlur(changeEventRef.current);

        // run the onchange event, which is necessary to update the innerValue of the form field
        // note that running formHelpers.setValue() in the doOnBlur function will not set the value correctly
        onChange(changeEventRef.current);
      }

      onBlur(e);
    },
    [onBlur, isReady, cancel, doOnBlur, onChange]
  );
  return [changeEventRef, handleOnBlur];
};

export default useDebounceWithOnBlur;
